export type VoiceAvatarStyle = 'none' | 'orb' | 'cube' | 'character';
export type VoiceAvatarState = 'idle' | 'listening' | 'thinking' | 'speaking';

export interface VoiceAvatarOptions {
  style: VoiceAvatarStyle;
  state?: VoiceAvatarState;
  primaryColor?: string;
  accentColor?: string;
  orbitEnabled?: boolean;
  orbitScale?: number;
  /** `character` style: portrait rendered on the stage. */
  imageUrl?: string;
}

const STATE_COLORS: Record<VoiceAvatarState, string> = {
  idle: '#94a3b8',
  listening: '#22c55e',
  thinking: '#eab308',
  speaking: '#f45b61',
};

type Point = readonly [number, number];

// Exact polygon geometry from the BitFractal cube SVG. Polygons that form one
// physical slab stay grouped so animation never cuts through a face.
const CUBE_SLABS: ReadonlyArray<ReadonlyArray<ReadonlyArray<Point>>> = [
  [
    [[109.434, 21.799], [99.229, 15.761], [26.494, 58.054], [36.587, 63.781]],
    [[36.577, 147.524], [41.039, 150.11], [41.039, 66.665], [36.396, 63.686]],
  ],
  [
    [[113.824, 24.394], [41.089, 66.687], [51.183, 72.415], [124.028, 30.432]],
    [[51.421, 156.253], [56.158, 158.839], [56.158, 75.393], [51.23, 72.415]],
  ],
  [
    [[139.084, 39.097], [128.88, 33.059], [56.145, 75.352], [66.207, 81.062], [66.398, 164.886], [71.135, 167.472], [71.135, 84.026], [66.249, 81.073]],
  ],
  [
    [[168.973, 56.41], [158.769, 50.372], [86.055, 92.654], [81.321, 89.736], [154.125, 47.778], [143.921, 41.74], [71.185, 84.034], [81.23, 89.734], [81.418, 173.519], [86.063, 176.105], [86.063, 92.682], [96.128, 98.393]],
  ],
  [
    [[100.979, 101.269], [100.979, 185.212], [173.682, 143.211], [173.782, 59.367]],
  ],
];

const CUBE_BOUNDS = { left: 26.494, top: 15.761, width: 147.288, height: 169.451 };

function mixHex(first: string, second: string, amount: number): string {
  const parse = (value: string) => /^#[0-9a-f]{6}$/i.test(value)
    ? [1, 3, 5].map(index => parseInt(value.slice(index, index + 2), 16))
    : [253, 98, 98];
  const a = parse(first);
  const b = parse(second);
  const channels = a.map((value, index) => Math.round(value + (b[index] - value) * amount));
  return `rgb(${channels[0]}, ${channels[1]}, ${channels[2]})`;
}

export class VoiceAvatarController {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly observer: ResizeObserver | null;
  private readonly reducedMotion: MediaQueryList | null;
  private frame = 0;
  private width = 0;
  private height = 0;
  private level = 0;
  private targetLevel = 0;
  private orbitPhase = 0;
  private lastTimestamp = 0;
  private style: VoiceAvatarStyle;
  private state: VoiceAvatarState;
  private primaryColor: string;
  private accentColor: string;
  private orbitEnabled: boolean;
  private orbitScale: number;
  private destroyed = false;
  private image: HTMLImageElement | null = null;
  private imageReady = false;

  constructor(canvas: HTMLCanvasElement, options: VoiceAvatarOptions) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable');
    this.canvas = canvas;
    this.context = context;
    this.style = options.style;
    this.state = options.state || 'idle';
    this.primaryColor = options.primaryColor || '#f45b61';
    this.accentColor = options.accentColor || '#60a5fa';
    this.orbitEnabled = options.orbitEnabled !== false;
    this.orbitScale = Math.min(1.8, Math.max(1, options.orbitScale ?? 1.25));
    if (options.imageUrl) this.loadImage(options.imageUrl);
    this.reducedMotion = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => this.resize()) : null;
    this.observer?.observe(canvas);
    this.resize();
    this.frame = requestAnimationFrame(this.draw);
  }

  private loadImage(url: string): void {
    const img = new Image();
    // Deliberately NOT crossOrigin: blob storage doesn't send CORS headers by
    // default, and requesting them makes the browser refuse to paint the image.
    // Tainting is harmless here — the canvas is only ever drawn to, never read.
    img.onload = () => { if (this.image === img) this.imageReady = true; };
    img.onerror = () => {
      if (this.image !== img) return;
      this.image = null;
      this.imageReady = false;
      try { console.warn('[AutoQM widget] voice avatar image failed to load:', url); } catch { /* */ }
    };
    this.image = img;
    img.src = url;
  }

  setStyle(style: VoiceAvatarStyle): void { this.style = style; }
  setState(state: VoiceAvatarState): void { this.state = state; }
  setLevel(level: number): void {
    this.targetLevel = this.state === 'speaking' ? Math.max(0, Math.min(1, level || 0)) : 0;
  }

  destroy(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    if (this.image) { this.image.onload = null; this.image.onerror = null; this.image = null; }
    this.imageReady = false;
    this.context.clearRect(0, 0, this.width, this.height);
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.width = Math.max(1, Math.round(rect.width));
    this.height = Math.max(1, Math.round(rect.height));
    this.canvas.width = Math.max(1, Math.round(this.width * ratio));
    this.canvas.height = Math.max(1, Math.round(this.height * ratio));
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  private draw = (timestamp: number): void => {
    if (this.destroyed) return;
    const reduced = !!this.reducedMotion?.matches;
    const response = this.targetLevel > this.level ? 0.34 : 0.11;
    this.level += (this.targetLevel - this.level) * response;
    // Orbits advance on their own accumulator rather than absolute time, so
    // speeding them up with the agent's voice never teleports the particles.
    const delta = this.lastTimestamp ? Math.min(0.05, (timestamp - this.lastTimestamp) / 1000) : 0;
    this.lastTimestamp = timestamp;
    if (!reduced) this.orbitPhase += delta * (1 + this.level * 2.6);
    this.context.clearRect(0, 0, this.width, this.height);
    if (this.style === 'orb') this.drawOrb(timestamp, reduced);
    if (this.style === 'cube') this.drawCube(timestamp, reduced);
    if (this.style === 'character') this.drawCharacter(timestamp, reduced);
    this.frame = requestAnimationFrame(this.draw);
  };

  private drawOrb(timestamp: number, reduced: boolean): void {
    const context = this.context;
    const centerX = this.width / 2;
    const centerY = this.height / 2;
    const minSize = Math.min(this.width, this.height);
    const time = reduced ? 0 : timestamp / 1000;
    const ambient = this.state === 'idle' ? 0.02 : 0.05;
    const pulse = reduced ? 0 : Math.sin(time * (this.state === 'thinking' ? 2.8 : 1.4)) * ambient;
    const radius = minSize * (0.22 + pulse + this.level * 0.08);
    const stateColor = STATE_COLORS[this.state];

    const glow = context.createRadialGradient(centerX, centerY, radius * 0.2, centerX, centerY, radius * 1.8);
    glow.addColorStop(0, this.primaryColor);
    glow.addColorStop(0.45, stateColor);
    glow.addColorStop(1, 'rgba(255,255,255,0)');
    context.globalAlpha = 0.32 + this.level * 0.35;
    context.fillStyle = glow;
    context.beginPath();
    context.arc(centerX, centerY, radius * 1.8, 0, Math.PI * 2);
    context.fill();

    const orbitScale = 1 + (this.orbitScale - 1) * 0.7;
    const swell = 1 + this.level * 0.16;
    const orbits = [
      { radiusX: radius * 1.62 * orbitScale * swell, radiusY: radius * 0.7 * orbitScale * swell, rotation: 0.42, speed: 1.45, color: this.accentColor },
      { radiusX: radius * 1.92 * orbitScale * swell, radiusY: radius * 0.86 * orbitScale * swell, rotation: -0.48, speed: -1.2, color: this.primaryColor },
    ];
    if (this.orbitEnabled) this.drawOrbitLayer(centerX, centerY, orbits, time, reduced, false, radius);

    const core = context.createRadialGradient(centerX - radius * 0.25, centerY - radius * 0.3, 1, centerX, centerY, radius);
    core.addColorStop(0, '#ffffff');
    core.addColorStop(0.22, this.accentColor);
    core.addColorStop(1, stateColor);
    context.globalAlpha = 1;
    context.fillStyle = core;
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.fill();
    if (this.orbitEnabled) this.drawOrbitLayer(centerX, centerY, orbits, time, reduced, true, radius);
    context.globalAlpha = 1;
  }

  /**
   * Static brand portrait, brought to life procedurally: a state-coloured halo
   * that swells with the agent's voice, plus a subtle level-driven scale on the
   * figure itself. The whole figure is always shown, fitted to the stage.
   */
  private drawCharacter(_timestamp: number, _reduced: boolean): void {
    const context = this.context;
    const image = this.image;
    const centerX = this.width / 2;
    const centerY = this.height / 2;
    const minSize = Math.min(this.width, this.height);
    const stateColor = STATE_COLORS[this.state];

    // Halo sits behind the portrait and carries the whole audio reaction, so
    // the avatar still reads as "live" even before the image has loaded.
    const haloRadius = minSize * (0.34 + this.level * 0.12);
    const halo = context.createRadialGradient(centerX, centerY, haloRadius * 0.25, centerX, centerY, haloRadius);
    halo.addColorStop(0, mixHex(stateColor, this.accentColor, 0.35));
    halo.addColorStop(0.55, stateColor);
    halo.addColorStop(1, 'rgba(255,255,255,0)');
    context.globalAlpha = 0.22 + this.level * 0.4;
    context.fillStyle = halo;
    context.beginPath();
    context.arc(centerX, centerY, haloRadius, 0, Math.PI * 2);
    context.fill();
    context.globalAlpha = 1;

    if (!image || !this.imageReady || !image.naturalWidth || !image.naturalHeight) return;

    const swell = 1 + this.level * 0.06;

    // Always the whole figure, scaled to the stage and standing on its floor —
    // compact just shows a smaller version of the same avatar.
    const fit = Math.min(this.width / image.naturalWidth, this.height / image.naturalHeight);
    const drawW = image.naturalWidth * fit * swell;
    const drawH = image.naturalHeight * fit * swell;
    context.drawImage(image, centerX - drawW / 2, this.height - drawH, drawW, drawH);
  }

  private drawCube(timestamp: number, reduced: boolean): void {
    const context = this.context;
    const minSize = Math.min(this.width, this.height);
    const time = reduced ? 0 : timestamp / 1000;
    const scale = Math.min(
      (this.width * 0.72) / CUBE_BOUNDS.width,
      (this.height * 0.78) / CUBE_BOUNDS.height,
    );
    const originX = (this.width - CUBE_BOUNDS.width * scale) / 2 - CUBE_BOUNDS.left * scale;
    const originY = (this.height - CUBE_BOUNDS.height * scale) / 2 - CUBE_BOUNDS.top * scale;
    const thinkingMotion = this.state === 'thinking' && !reduced ? Math.sin(time * 2.4) * minSize * 0.006 : 0;

    const glow = context.createRadialGradient(this.width / 2, this.height / 2, minSize * 0.08, this.width / 2, this.height / 2, minSize * 0.42);
    glow.addColorStop(0, `${this.accentColor}38`);
    glow.addColorStop(1, `${this.accentColor}00`);
    context.fillStyle = glow;
    context.fillRect(0, 0, this.width, this.height);

    const spacing = 1 + (this.orbitScale - 1) * 0.55;
    const swell = 1 + this.level * 0.16;
    const cubeOrbits = [
      { radiusX: minSize * 0.42 * spacing * swell, radiusY: minSize * 0.17 * spacing * swell, rotation: 0.46, speed: 1.3, color: this.accentColor },
      { radiusX: minSize * 0.47 * spacing * swell, radiusY: minSize * 0.2 * spacing * swell, rotation: -0.43, speed: -1.08, color: this.primaryColor },
    ];
    if (this.orbitEnabled) this.drawOrbitLayer(this.width / 2, this.height / 2, cubeOrbits, time, reduced, false, minSize * 0.22);

    context.save();
    CUBE_SLABS.forEach((polygons, slabIndex) => {
      const phase = time * 8 - slabIndex * 0.72;
      const audioLift = reduced ? 0 : Math.sin(phase) * this.level * minSize * 0.036;
      const settle = thinkingMotion * (slabIndex - 2);
      const offsetY = audioLift + settle;
      context.fillStyle = mixHex(this.primaryColor, this.accentColor, slabIndex * 0.055);
      for (const polygon of polygons) {
        context.beginPath();
        polygon.forEach(([pointX, pointY], pointIndex) => {
          const x = originX + pointX * scale;
          const y = originY + pointY * scale + offsetY;
          if (pointIndex === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        });
        context.closePath();
        context.fill();
      }
    });
    context.restore();
    if (this.orbitEnabled) this.drawOrbitLayer(this.width / 2, this.height / 2, cubeOrbits, time, reduced, true, minSize * 0.22);
  }

  private drawOrbitLayer(
    centerX: number,
    centerY: number,
    orbits: Array<{ radiusX: number; radiusY: number; rotation: number; speed: number; color: string }>,
    time: number,
    reduced: boolean,
    front: boolean,
    objectRadius: number,
  ): void {
    const context = this.context;
    context.save();
    const baseWidth = Math.max(1, Math.min(this.width, this.height) * 0.005);
    context.lineWidth = baseWidth * (1 + this.level * 1.8);
    orbits.forEach((orbit, index) => {
      const rotation = orbit.rotation + (reduced ? 0 : Math.sin(time * 0.3 + index) * 0.035);
      context.globalAlpha = front ? 0.5 + this.level * 0.25 : 0.22 + this.level * 0.16;
      context.strokeStyle = orbit.color;
      context.beginPath();
      context.ellipse(
        centerX,
        centerY,
        orbit.radiusX,
        orbit.radiusY,
        rotation,
        front ? 0 : Math.PI,
        front ? Math.PI : Math.PI * 2,
      );
      context.stroke();

      const angle = reduced ? index * Math.PI : this.orbitPhase * orbit.speed + index * Math.PI;
      const particleInFront = Math.sin(angle) >= 0;
      if (particleInFront !== front) return;
      const localX = Math.cos(angle) * orbit.radiusX;
      const localY = Math.sin(angle) * orbit.radiusY;
      const particleX = centerX + localX * Math.cos(rotation) - localY * Math.sin(rotation);
      const particleY = centerY + localX * Math.sin(rotation) + localY * Math.cos(rotation);
      context.globalAlpha = front ? 0.95 : 0.42;
      context.fillStyle = index ? this.accentColor : '#ffffff';
      context.beginPath();
      context.arc(particleX, particleY, Math.max(2, objectRadius * (0.045 + this.level * 0.06)), 0, Math.PI * 2);
      context.fill();
    });
    context.restore();
  }
}
